import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { REGIONES, TASK_TYPES } from "@/lib/constants";
import { ArrowLeft, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function DelegationEdit() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notEditable, setNotEditable] = useState("");

  useEffect(() => {
    let mounted = true;
    api.get(`/delegations/${id}`)
      .then(({ data }) => {
        if (!mounted) return;
        // GET /delegations/:id devuelve { delegation, bids }
        const d = data?.delegation || data;
        // Guardas de acceso y estado
        if (d.owner_id !== user?.id) {
          setNotEditable("Sólo el mandante creador puede editar esta audiencia.");
        } else if (d.status !== "open") {
          setNotEditable("Esta audiencia ya cuenta con un colega asignado. Para modificar información relevante, contacta al colega o solicita revisión.");
        } else if (["in_escrow", "released"].includes(d.payment_status)) {
          setNotEditable("Esta audiencia ya está pagada. No es posible modificarla directamente.");
        }
        setForm({
          title: d.title || "",
          task_type: d.task_type || "audiencia",
          court: d.court || "",
          case_number: d.case_number || "",
          region: d.region || "Metropolitana",
          comuna: d.comuna || "",
          address: d.address || "",
          date: d.date || "",
          time: d.time || "",
          fee_clp: d.fee_clp || 0,
          notes: d.notes || "",
        });
      })
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
    return () => { mounted = false; };
  }, [id, user?.id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setOkMsg(""); setSaving(true);
    try {
      await api.put(`/delegations/${id}`, { ...form, fee_clp: Number(form.fee_clp) });
      setOkMsg("Audiencia actualizada correctamente");
      setTimeout(() => nav(`/app/delegaciones/${id}`), 1200);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  if (loading) return <div className="container-app py-16 text-muted-foreground">Cargando…</div>;
  if (!form) return <div className="container-app py-16 text-[hsl(var(--destructive))]">{error || "No se pudo cargar la audiencia."}</div>;

  return (
    <div className="container-app py-12 max-w-3xl" data-testid="edit-delegation-page">
      <Link to={`/app/delegaciones/${id}`} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="edit-back">
        <ArrowLeft size={14}/> Volver al detalle
      </Link>
      <div className="label-eyebrow mt-4">Editar publicación</div>
      <h1 className="serif text-5xl mt-3">Editar audiencia</h1>
      <p className="mt-2 text-muted-foreground text-sm">
        Puedes corregir todos los datos de la publicación mientras esté abierta y sin colega asignado.
      </p>

      {notEditable && (
        <div className="mt-6 panel p-5 border-amber-400/30 bg-amber-400/5" data-testid="edit-blocked-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle size={16} className="text-amber-300 shrink-0 mt-0.5"/>
            <div className="text-sm">{notEditable}</div>
          </div>
        </div>
      )}

      <form onSubmit={submit} className={`mt-8 panel p-8 grid sm:grid-cols-2 gap-5 ${notEditable ? "opacity-50 pointer-events-none" : ""}`} data-testid="edit-delegation-form" aria-disabled={!!notEditable}>
        <Field label="Título"><input className="field" required value={form.title} onChange={(e)=>set("title", e.target.value)} data-testid="edit-title"/></Field>
        <Field label="Tipo">
          <select className="field" value={form.task_type} onChange={(e)=>set("task_type", e.target.value)} data-testid="edit-type">
            {TASK_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="Tribunal" className="sm:col-span-2"><input className="field" required value={form.court} onChange={(e)=>set("court", e.target.value)} data-testid="edit-court"/></Field>
        <Field label="Causa / RIT"><input className="field" value={form.case_number} onChange={(e)=>set("case_number", e.target.value)} data-testid="edit-case"/></Field>
        <Field label="Región">
          <select className="field" value={form.region} onChange={(e)=>set("region", e.target.value)} data-testid="edit-region">
            {REGIONES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Comuna"><input className="field" required value={form.comuna} onChange={(e)=>set("comuna", e.target.value)} data-testid="edit-comuna"/></Field>
        <Field label="Dirección"><input className="field" value={form.address} onChange={(e)=>set("address", e.target.value)} data-testid="edit-address"/></Field>
        <Field label="Fecha"><input className="field" type="date" required value={form.date} onChange={(e)=>set("date", e.target.value)} data-testid="edit-date"/></Field>
        <Field label="Hora (24h, ej: 09:30 · 14:00 · 18:45)">
          <input className="field" type="time" step="60" required value={form.time} onChange={(e)=>set("time", e.target.value)} data-testid="edit-time"/>
        </Field>
        <Field label="Honorario (CLP)" className="sm:col-span-2">
          <input className="field" type="number" min={0} step={1000} required value={form.fee_clp} onChange={(e)=>set("fee_clp", e.target.value)} data-testid="edit-fee"/>
        </Field>
        <Field label="Notas / instrucciones" className="sm:col-span-2">
          <textarea className="field" rows={4} value={form.notes} onChange={(e)=>set("notes", e.target.value)} data-testid="edit-notes"/>
        </Field>
        {error && <div className="sm:col-span-2 text-sm text-[hsl(var(--destructive))]" data-testid="edit-error">{error}</div>}
        {okMsg && (
          <div className="sm:col-span-2 text-sm text-emerald-300 inline-flex items-center gap-2" data-testid="edit-ok">
            <CheckCircle2 size={14}/> {okMsg}
          </div>
        )}
        <div className="sm:col-span-2 flex items-center gap-3">
          <button disabled={saving || !!notEditable} className="btn-primary disabled:opacity-60" data-testid="edit-submit">
            {saving ? "Guardando…" : "Guardar cambios"}
          </button>
          <button type="button" onClick={()=>nav(`/app/delegaciones/${id}`)} className="btn-ghost">Cancelar</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="label-eyebrow">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
